import * as FormData from './form-data';

export interface IFormSubmissionDto {
    camper: ICamper;
    emergency: IEmergency;
    health: IHealth;
    waivers: IWaivers;
}

/**
 * Camper information.
 */
export interface ICamper {
    basicInformation: FormData.IBasicInformation;
    parentInformation: FormData.IParentInformationFormSubmit;
    siblings: Array<FormData.ISibling>;
    aboutMyChild: FormData.IAboutMyChild;
}

export interface ParentSP {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    homePhone: string;
    workPhone: string;
    cellPhone: string;
    primaryPhone: string;
    email: string;

    correspondence: boolean;
}

/**
 * Emergency information.
 */
export interface IEmergency {
    contacts: Array<FormData.IEmergencyContact>;
    healthCare: FormData.IHealthCareContact;
    insurance: FormData.IInsuranceInformation;
    pickups: Array<FormData.IPickupAuthorization>;
    permissions: FormData.ITransportationPermission;
}

/**
 * Health information.
 */
export interface IHealth {
    allergySection: FormData.IAllergySection;
    dietarySection: FormData.IDietaryRestrictionSection;
    medicalConditionsSection: FormData.IMedicalConditionSection;
    specialNeedsSection: FormData.ISpecialNeedsSection;
    immunizations: boolean;
    medicationsSection: FormData.IMedicationSection;
    administeringMedication: FormData.IAdministeringMedication;
}

export interface IWaivers {
    mediaRelease: FormData.IWaiver;
    liability: FormData.IWaiver;
    fieldTripPermission: FormData.IWaiver;
    emergencyRoomRelease: FormData.IWaiver;
    wellChild: FormData.IWaiver;
}

export interface IWaiverApiText {
    mediaReleaseText: string;
    liabilityText: string;
    fieldTripText: string;
    hospitalReleaseText: string;
    wellChildText: string;
}

export const defaultWaiverApiText: IWaiverApiText = {
    mediaReleaseText: '',
    liabilityText: '',
    fieldTripText: '',
    hospitalReleaseText: '',
    wellChildText: ''
};

export interface PreviouslyRegisteredStudent {
    customer_no: number;
    student_cust_no: number;
    opt_1: number;
    opt_1_desc: string;
    opt_2: number;
    opt_2_desc: string;
    opt_3: number;
    opt_3_desc: string;
    opt_4: number;
    opt_4_desc: string;
    deposit_amt: number;
    fname: string;
    lname: string;
    nickname: string;
    dob: Date;
    sex: string;
    t_shirt_size: string;
    grade: number;
    school: string;
    p1_fname: string;
    p1_lname: string;
    p1_street_add: string;
    p1_state: string;
    p1_postal_code: string;
    p1_city: string;
    p1_phone_home: string;
    p1_phone_work: string;
    p1_phone_mobile: string;
    p1_phone_primary: string;
    p1_email: string;
    p1_correspond_with: string;
    p2_fname: string;
    p2_lname: string;
    p2_street_add: string;
    p2_state: string;
    p2_postal_code: string;
    p2_city: string;
    p2_phone_home: string;
    p2_phone_work: string;
    p2_phone_mobile: string;
    p2_phone_primary: string;
    p2_email: string;
    p2_correspond_with: string;
    sib1_fname: string;
    sib1_lname: string;
    sib1_birthdate: Date;
    sib1_age: number;
    sib2_fname: string;
    sib2_lname: string;
    sib2_birthdate: Date;
    sib2_age: number;
    sib3_fname: string;
    sib3_lname: string;
    sib3_birthdate: Date;
    sib3_age: number;
    sib4_fname: string;
    sib4_lname: string;
    sib4_birthdate: Date;
    sib4_age: number;
    theatre_ind: string;
    music_ind: string;
    visual_arts_ind: string;
    dance_ind: string;
    other_interest_ind: string;
    other_interest_text: string;
    notes: string;
    ec1_fname: string;
    ec1_lname: string;
    ec1_relationship: string;
    ec1_phone: string;
    ec2_fname: string;
    ec2_lname: string;
    ec2_relationship: string;
    ec2_phone: string;
    ec3_fname: string;
    ec3_lname: string;
    ec3_relationship: string;
    ec3_phone: string;
    physician_name: string;
    physician_phone: string;
    dentist_name: string;
    dentist_phone: string;
    health_plan_desc: string;
    health_plan_name: string;
    health_plan_policy_ID: string;
    pu1_fname: string;
    pu1_lname: string;
    pu1_relationship: string;
    pu1_phone: string;
    pu2_fname: string;
    pu2_lname: string;
    pu2_relationship: string;
    pu2_phone: string;
    pu3_fname: string;
    pu3_lname: string;
    pu3_relationship: string;
    pu3_phone: string;
    carpool_ind: string;
    public_transit_ind: string;
    walk_bike_ind: string;
    peanut_allergy_ind: string;
    peanut_allergy_desc: string;
    treenut_allergy_ind: string;
    treenut_allergy_desc: string;
    food_allergy_ind: string;
    food_allergy_desc: string;
    meds_allergy_ind: string;
    meds_allergy_desc: string;
    bee_allergy_ind: string;
    bee_allergy_desc: string;
    plant_allergy_ind: string;
    plant_allergy_desc: string;
    other_allergy_ind: string;
    other_allergy_desc: string;
    epipen_has_ind: string;
    epipen_which_allergy_str: string;
    epipen_can_self_treat_ind: string;
    epipen_storage: string;
    allergen_free_lunch_ind: string;
    gluten_free_ind: string;
    vegetarian_ind: string;
    vegan_ind: string;
    lactose_intol_ind: string;
    kosher_ind: string;
    pizza_ind: string;
    asthma_ind: string;
    asthma_has_inhaler: string;
    asthma_can_self_treat: string;
    asthma_inhaler_storage: string;
    diabetes_ind: string;
    seizures_ind: string;
    dizziness_ind: string;
    dizziness_desc: string;
    headaches_ind: string;
    headaches_desc: string;
    med_surg_hist_ind: string;
    med_surg_hist_desc: string;
    other_med_info_ind: string;
    other_med_info_desc: string;
    unsafe_activities_desc: string;
    call_ind: string;
    special_needs_ind: string;
    special_needs_desc: string;
    immunizations_uptodate_ind: string;
    m1_medication_name: string;
    m1_dosage: string;
    m1_purpose: string;
    m2_medication_name: string;
    m2_dosage: string;
    m2_purpose: string;
    m3_medication_name: string;
    m3_dosage: string;
    m3_purpose: string;
    m4_medication_name: string;
    m4_dosage: string;
    m4_purpose: string;
    medications_take_at_camp_str: string;
    allow_otc_str: string;
    allow_otc_initials: string;
    auth_media_ind: string;
    auth_media_initials: string;
    auth_liability_ind: string;
    auth_liability_initials: string;
    auth_fieldtrip_ind: string;
    auth_fieldtrip_initials: string;
    auth_hospital_ind: string;
    auth_hospital_initials: string;
    auth_wellness_ind: string;
    auth_wellness_initials: string;
}


// WEBPACK FOOTER //
// ./app/classes/dto.ts